/**
 * ADLSectionNA.jsx
 * CCH Care — Phase 5d
 *
 * Reusable wrapper that adds N/A and "Nothing to Report" UI
 * to any ADL section form.
 *
 * Retract reachability fix (2026-06-11):
 *   Attesting "Nothing to report" auto-signs the section (the page calls
 *   signSilently right after attest), so an attested section is ALWAYS
 *   signed. The Undo button used to be gated on `!isSectionSigned`, which
 *   meant it never appeared for an attested section — and Amend is hidden
 *   on attested sections too — leaving a wrong attestation with no
 *   correction path. Undo now shows whenever an attestation exists and
 *   onRetract is wired; the backend (attestations.php retract) enforces
 *   the actual rules (open window, non-terminal record) and the button
 *   surfaces any 409 (e.g. "window has closed — ask a supervisor to
 *   reopen") inline. `isSectionSigned` still gates the OFFER of a new
 *   attestation below (you can't attest an already-signed section).
 *
 * ESLint fixes:
 * - no-unescaped-entities: replaced " with &ldquo;/&rdquo; in JSX text
 *
 * Dark mode (2026-05-04): replaced '#F8FAFC' N/A banner background
 *   with COLORS.bg, added COLORS.surface/text to attestation input.
 *   Amber warning colours preserved as intentional semantic UI.
 *
 * 2026-05-07 — Attestation visual revisit:
 *   The previous "preserve amber" decision themed poorly in dark mode
 *   (cream card on a dark page, brown text barely legible). Replaced
 *   the hardcoded amber-cream surfaces (#FFFBEB bg, #FDE68A border,
 *   #92400E text, #D97706 confirm) with a callout-stripe pattern:
 *   neutral COLORS.surface card + 4px COLORS.warning left stripe +
 *   warning-colored title and CTA. The "this is significant, pause
 *   and read" semantic now lives in the stripe + warning title +
 *   orange CTA, rather than in a full warm-tinted background.
 *   Themes correctly in both modes without inventing new tokens.
 */

import { useState } from 'react';
import { COLORS } from '../../config/constants';
import { formatTime } from '../../utils/formatters';

export default function ADLSectionNA({
  section,
  isNA               = false,
  naReason           = '',
  supportsAttestation = false,
  attestation        = null,
  isSectionSigned    = false,
  onAttest,
  onRetract,
}) {
  const [showAttestForm, setShowAttestForm] = useState(false);
  const [attestNotes, setAttestNotes]       = useState('');
  const [attesting, setAttesting]           = useState(false);
  const [attestError, setAttestError]       = useState('');

  // Undo (retract) state — the retract can be rejected by the backend
  // (closed window, terminal record), so we await it and surface the message.
  const [retracting, setRetracting]   = useState(false);
  const [retractError, setRetractError] = useState('');

  // Suppress unused-vars for section — reserved for future per-section logic
  void section;

  async function handleUndo() {
    if (!onRetract) return;
    setRetractError('');
    setRetracting(true);
    try {
      await onRetract();
    } catch (e) {
      setRetractError(
        e?.response?.data?.error
        || e?.message
        || 'Could not undo — the documentation window may have closed. Ask a supervisor to reopen this record.'
      );
    } finally {
      setRetracting(false);
    }
  }

  // ── N/A Banner ──────────────────────────────────────────────
  if (isNA) {
    return (
      <div style={{
        background: COLORS.bg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        padding: '12px 14px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}>
        <span style={{ fontSize: 18, flexShrink: 0 }}>🚫</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textMuted }}>
            Not applicable for this resident
          </div>
          {naReason && (
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
              {naReason}
            </div>
          )}
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
            Set in care plan &middot; Contact a supervisor to change
          </div>
        </div>
      </div>
    );
  }

  // ── Nothing-to-report attestation ───────────────────────────
  if (!supportsAttestation) return null;

  // Already attested — neutral surface with warning accent stripe.
  // Undo is offered regardless of signed state (an attested section is
  // always auto-signed); the backend decides whether it's actually
  // retractable and we surface any rejection inline.
  if (attestation) {
    const time = attestation.attested_at
      ? formatTime(attestation.attested_at)
      : '';

    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderLeft: `4px solid ${COLORS.warning}`,
          borderRadius: 8,
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>📝</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.warning }}>
              Nothing to report this shift
            </div>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>
              Attested {time}
              {attestation.attested_by_name ? ` · ${attestation.attested_by_name}` : ''}
              {attestation.notes
                ? <> &middot; &ldquo;{attestation.notes}&rdquo;</>
                : null}
            </div>
          </div>
          {onRetract && (
            <button
              onClick={handleUndo}
              disabled={retracting}
              style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 6,
                background: 'none', color: COLORS.textMuted,
                border: `1px solid ${COLORS.border}`,
                cursor: retracting ? 'not-allowed' : 'pointer',
                flexShrink: 0, opacity: retracting ? 0.7 : 1,
              }}
            >
              {retracting ? 'Undoing\u2026' : 'Undo'}
            </button>
          )}
        </div>
        {retractError && (
          <div style={{ fontSize: 11, color: COLORS.danger, marginTop: 6 }}>
            {retractError}
          </div>
        )}
      </div>
    );
  }

  // ── Offer attestation button ─────────────────────────────────
  // Can't attest an already-signed section.
  if (isSectionSigned) return null;

  async function handleAttest() {
    setAttestError('');
    setAttesting(true);
    try {
      await onAttest(attestNotes);
      setShowAttestForm(false);
      setAttestNotes('');
    } catch (e) {
      setAttestError(e?.response?.data?.error || e.message || 'Failed to record attestation');
    } finally {
      setAttesting(false);
    }
  }

  return (
    <div style={{ marginBottom: 12 }}>
      {!showAttestForm ? (
        <button
          onClick={() => setShowAttestForm(true)}
          style={{
            width: '100%', padding: '9px 0', borderRadius: 8,
            fontSize: 12, fontWeight: 600,
            background: 'none',
            border: `1px dashed ${COLORS.border}`,
            color: COLORS.textMuted,
            cursor: 'pointer',
          }}
        >
          📝 Nothing to report this shift
        </button>
      ) : (
        <div style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderLeft: `4px solid ${COLORS.warning}`,
          borderRadius: 8,
          padding: '12px 14px',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.warning, marginBottom: 8 }}>
            Record &ldquo;Nothing to Report&rdquo;
          </div>
          <div style={{ fontSize: 12, color: COLORS.text, marginBottom: 10, lineHeight: 1.5 }}>
            This attests that you assessed the resident and there was nothing to document
            for this section during this shift.
          </div>
          <input
            type="text"
            value={attestNotes}
            onChange={e => setAttestNotes(e.target.value)}
            placeholder="Optional note (e.g. No BM this shift, will monitor)"
            style={{
              width: '100%', padding: '7px 10px', borderRadius: 6,
              border: `1px solid ${COLORS.border}`, fontSize: 12,
              background: COLORS.surface, color: COLORS.text,
              boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 8,
            }}
          />
          {attestError && (
            <div style={{ fontSize: 12, color: COLORS.danger, marginBottom: 6 }}>
              {attestError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => { setShowAttestForm(false); setAttestNotes(''); setAttestError(''); }}
              disabled={attesting}
              style={{
                flex: 1, padding: '7px 0', borderRadius: 6,
                background: 'none',
                border: `1px solid ${COLORS.border}`,
                color: COLORS.textMuted,
                fontSize: 12, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAttest}
              disabled={attesting}
              style={{
                flex: 2, padding: '7px 0', borderRadius: 6,
                background: COLORS.warning, color: '#fff',
                border: 'none', fontSize: 12, fontWeight: 600,
                cursor: attesting ? 'not-allowed' : 'pointer',
                opacity: attesting ? 0.7 : 1,
              }}
            >
              {attesting ? 'Recording\u2026' : 'Confirm \u2014 Nothing to Report'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}